import { useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { FiSearch, FiUser, FiMenu, FiX } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import { selectCurrentToken } from "../../features/auth/authSlice";
import useAuth from "../../hooks/useAuth";
import { useGetMeQuery } from "../../features/api/userApiSlice";
import "./navStyles.scss";
import { useSendLogoutMutation } from "../../features/api/authApiSlice";
import { useGetFilteredVideosQuery } from "../../features/api/videoApiSlice";
import VideoSearch from "../../features/video/VideoSearch";
import NotificationDropdown from "../notification/NotificationCenter";

const GeneralNavbar = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const token = useSelector(selectCurrentToken);
  const [sendLogout] = useSendLogoutMutation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const suggestionsRef = useRef();

  const { data: user } = useGetMeQuery(undefined, {
    skip: !token,
  });

  const { data: searchData } = useGetFilteredVideosQuery(
    { searchTerm, page: 1, limit: 5 },
    { skip: !searchTerm }
  );
  const suggestions = searchData?.videos || [];

  const handleSuggestionClick = (video) => {
    setSearchOpen(false);
    setSearchTerm("");
    navigate(`/watch/${video.slug || video._id}`);
  };

  const handleKeyDown = (e) => {
    if (!suggestions.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev === 0 ? suggestions.length - 1 : prev - 1
      );
    }
    if (e.key === "Enter") {
      e.preventDefault();
      handleSuggestionClick(suggestions[highlightedIndex]);
    }
  };

  const handleLogout = async () => {
    try {
      await sendLogout();
      localStorage.removeItem("accessToken");
      //toast.success(response?.data?.message);
    } catch (error) {
      return toast.error(error?.data?.message);
    }
  };

  // Define menu items
  const menuItems = [
    { path: "/", label: "Home" },
    { path: "/my-list", label: "My List", requiresAuth: true },
    // { path: "/shop", label: "Shop" },
    // { path: "/products", label: "Products" },
    { path: "/plans", label: "Plans" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
    { path: "/faq", label: "Faq" },
  ];

  // Filter out items that require login
  const visibleItems = menuItems.filter(
    (item) => !item.requiresAuth || isLoggedIn
  );

  return (
    <>
      <div className="mobile-logo">
        <Link to="/">
          <img
            src="https://woodsmithstorecontent.s3.us-east-2.amazonaws.com/logo/enotes_logo.png"
            alt="Twiga web design logo"
          />
        </Link>
      </div>
      <nav className="navbar">
        {/* Hamburger (mobile only) */}
        <button
          className="hamburger"
          onClick={() => setMobileOpen((prev) => !prev)}
        >
          {mobileOpen ? <FiX /> : <FiMenu />}
        </button>
        <div className="navbar__logo">
          <Link to="/">
            <img
              src="https://woodsmithstorecontent.s3.us-east-2.amazonaws.com/logo/enotes_logo.png"
              alt="Twiga web design logo"
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <ul className="navbar__links">
          {visibleItems.map(({ path, label }) => (
            <li key={path}>
              <NavLink
                to={path}
                className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }
              >
                {label}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Right Side (desktop) */}
        <div className="navbar__actions">
          <div className="nav_right_buttons">
            <button
              className="icon-btn"
              onClick={() => setSearchOpen((prev) => !prev)}
              // The searchOpen state indicates if the search is open (true) or closed (false)
              aria-expanded={searchOpen}
              // This connects the button to the content it controls
              aria-controls="search-overlay"
              // This provides a text label for screen readers
              aria-label="Toggle Search"
            >
              <FiSearch />
            </button>
            {isLoggedIn && <NotificationDropdown />}
          </div>

          {/* Search Overlay */}
          {searchOpen && (
            <VideoSearch
              id="search-overlay"
              filtersVisible={searchOpen}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              setHighlightedIndex={setHighlightedIndex}
              handleSuggestionClick={handleSuggestionClick}
              suggestions={suggestions}
              highlightedIndex={highlightedIndex}
              handleKeyDown={handleKeyDown}
              suggestionsRef={suggestionsRef}
            />
          )}

          {isLoggedIn ? (
            <div className="user-menu">
              <button
                className="user-btn"
                onClick={() => setMenuOpen((prev) => !prev)}
              >
                <FiUser /> <span>{user?.firstName}</span>
              </button>

              <AnimatePresence>
                {menuOpen && (
                  <motion.ul
                    className="dropdown"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => setMenuOpen((prev) => !prev)}
                  >
                    <li className="dropdown__header">{user?.firstName}</li>
                    {user?.roles?.includes("Admin") && (
                      <li>
                        <NavLink to="/admin">Admin Dashboard</NavLink>
                      </li>
                    )}
                    {user?.roles?.includes("User") && (
                      <li>
                        <Link to="/user/dashboard">User Dashboard</Link>
                      </li>
                    )}
                    <li>
                      <NavLink to="/profile">My Profile</NavLink>
                    </li>
                    <li>
                      <NavLink to="/contact">Support</NavLink>
                    </li>
                    <li>
                      <button className="logout-btn" onClick={handleLogout}>
                        Logout
                      </button>
                    </li>
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <>
              <button
                className="nav-login-btn"
                onClick={() => navigate("/login")}
              >
                Login
              </button>
              <button
                className="nav-member-btn"
                onClick={() => navigate("/member")}
              >
                Become a Member
              </button>
            </>
          )}
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              className="mobile-menu"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.3 }}
            >
              <ul>
                <div className="hamburger-toggle-close">
                  <button
                    className="hamburger-close"
                    onClick={() => setMobileOpen((prev) => !prev)}
                  >
                    {mobileOpen ? <FiX /> : <FiMenu />}
                  </button>
                </div>
                <li>
                  <NavLink to="/" onClick={() => setMobileOpen(false)}>
                    Home
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/my-list" onClick={() => setMobileOpen(false)}>
                    My List
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/about" onClick={() => setMobileOpen(false)}>
                    About
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/faq" onClick={() => setMobileOpen(false)}>
                    FAQ
                  </NavLink>
                </li>

                <hr />

                {isLoggedIn ? (
                  <>
                    <li className="mobile-user">{user?.firstName}</li>
                    {user?.roles?.includes("Admin") && (
                      <li>
                        <NavLink
                          to="/admin"
                          onClick={() => setMobileOpen(false)}
                        >
                          Admin Dashboard
                        </NavLink>
                      </li>
                    )}
                    {user?.roles?.includes("User") && (
                      <li>
                        <Link to="/user/dashboard">User Dashboard</Link>
                      </li>
                    )}
                    <li>
                      <NavLink
                        to="/profile"
                        onClick={() => setMobileOpen(false)}
                      >
                        My Profile
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to="/membership"
                        onClick={() => setMobileOpen(false)}
                      >
                        Manage Membership
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to="/security"
                        onClick={() => setMobileOpen(false)}
                      >
                        Security Settings
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to="/support"
                        onClick={() => setMobileOpen(false)}
                      >
                        Support
                      </NavLink>
                    </li>
                    <li>
                      <button className="logout-btn">Logout</button>
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      <NavLink to="/login" onClick={() => setMobileOpen(false)}>
                        Login
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to="/become-member"
                        onClick={() => setMobileOpen(false)}
                      >
                        Become a Member
                      </NavLink>
                    </li>
                  </>
                )}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default GeneralNavbar;
